import { Box, Typography } from "@mui/material"
import { ScheduleIntroduction } from "routes/schedule/scheduleIntroduction"
import { Bound } from "lib/@components/binding/Bound"
import { TaskList } from "./schedule-details"
import { SchedulePDFHeader } from "./schedule-pdf-header"
import { BusyHidden } from "event-definitions"
import { delay } from "lib/delay"
import { PrintableStack } from "lib/@components/pdf/printable-stack"
import { Watermark } from "lib/@components/watermark/watermark"
import { getClientProfile } from "routes/client-admin/plugins/profile/controller/get-client-profile"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { getCurrentUser } from "controller/getCurrentUser"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import {
    formatCopyrightYear,
    getPrintedByText,
    getUncontrolledCopyDisclaimer,
} from "routes/schedule/schedule-pdf-printer"
import { SchedulePDFFooterQRCode } from "routes/schedule/schedule-pdf-footer-qr-code"
import {
    getSchedulePDFFooterContentSx,
    getSchedulePDFPrintPageSx,
    getSchedulePDFScheduleUrl,
} from "routes/schedule/schedule-pdf-presenters"
import { COMPANY_LEGAL_NAME } from "library/SFG20/constants.mjs"

export function SchedulePDFDisplay({ schedule, id }) {
    const profile = getClientProfile.useResults({}, withInitialValue(IS_LOADING))
    const currentUser = getCurrentUser.useResults(withInitialValue(IS_LOADING))

    BusyHidden.useEvent(async (hidden) => {
        if (hidden) {
            const searchParams = new URLSearchParams(window.location.search)
            const shouldPrint = searchParams.has("print")
            if (shouldPrint) {
                await delay(500)
                window.print()
            }
        }
    })

    if (profile === IS_LOADING || currentUser === IS_LOADING) return <LoadingScreen />
    const printedBy = getPrintedByText(currentUser)
    const printedOn = new Date()
    const copyrightYear = formatCopyrightYear(printedOn)
    const disclaimer = getUncontrolledCopyDisclaimer({ companyName: profile?.name, printedBy, printedOn })
    const scheduleUrl = getSchedulePDFScheduleUrl(window.location.href)

    return (
        <Bound key={id}>
            <Box className="schedule-pdf-print-page" data-cy="schedule-pdf-print-page" sx={getSchedulePDFPrintPageSx()}>
                <table>
                    <thead className="print-header-container" aria-hidden="true">
                        <tr>
                            <td />
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <PrintableStack key={schedule._id}>
                                    {!!schedule.retired && <Watermark text="Retired" sx={{ position: "fixed" }} />}
                                    <Box
                                        className="schedule-details styled-bullets"
                                        sx={{
                                            overflowX: "hidden",
                                            pr: 0,
                                            width: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <SchedulePDFHeader
                                            schedule={schedule}
                                            profile={profile}
                                            printedOn={printedOn}
                                        />
                                        <ScheduleIntroduction schedule={schedule} />
                                        <TaskList schedule={schedule} />
                                    </Box>
                                </PrintableStack>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot className="print-footer-container" />
                </table>
                <Box className="print-footer">
                    <Box sx={getSchedulePDFFooterContentSx()}>
                        <Box sx={{ lineHeight: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                &copy; {COMPANY_LEGAL_NAME} {copyrightYear}. All rights reserved. {disclaimer} It
                                remains commercial in confidence. SFG20&reg; and Facilities-iQ&reg; are registered trade
                                marks of {COMPANY_LEGAL_NAME}.
                            </Typography>
                        </Box>
                        <SchedulePDFFooterQRCode badgeUrl={profile?.membershipBadgeUrl} scheduleUrl={scheduleUrl} />
                    </Box>
                </Box>
            </Box>
        </Bound>
    )
}
